import { createContext, useContext, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';

/**
 * The page-header contract. Adopting the prototype chrome, the page **title +
 * subtitle + actions** live in the sticky top bar (not the scrolling content).
 * Each routed page still declares its header by rendering `<PageHeader/>`; under
 * the shell that registration flows here and the {@link TopBar} renders it.
 */
export interface PageHeaderState {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}

/**
 * A tiny external store the page writes to and the TopBar subscribes to. Using
 * an external store (rather than lifting state into `AppShell`) is deliberate:
 * the writer (the routed page, under the `<Outlet/>`) is NOT a subscriber, so
 * registering a header never re-renders the page — only the TopBar re-renders.
 * That avoids the set-state-in-render feedback loop a context-state approach
 * would create (page write → AppShell re-render → page re-render → write …).
 */
let current: PageHeaderState | null = null;
const listeners = new Set<() => void>();

export const pageHeaderStore = {
  set(next: PageHeaderState | null): void {
    current = next;
    listeners.forEach((listener) => listener());
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getSnapshot(): PageHeaderState | null {
    return current;
  },
};

/** The current registered page header (TopBar consumer). */
export function usePageHeaderState(): PageHeaderState | null {
  return useSyncExternalStore(pageHeaderStore.subscribe, pageHeaderStore.getSnapshot, () => null);
}

/**
 * True when rendered inside the authenticated shell (which hosts the TopBar that
 * renders the page header). When false — standalone/tests — `PageHeader` renders
 * its title/subtitle inline instead, so it still shows and unit tests still find
 * it. `AppShell` provides `true`.
 */
export const InShellContext = createContext(false);

export function useInShell(): boolean {
  return useContext(InShellContext);
}
